import { inject, Injectable } from '@angular/core';
import { LocalStorageDataAccess } from '@softline/core';
import { UserInfoService } from '@softline/auth';
import { FilterTemplate } from '../types/filter-template';
import { FilterTemplateRepository } from './filter-template.repository';

const KEY_PREFIX = 'filter-template';

@Injectable()
export class LocalStorageFilterTemplateRepository extends FilterTemplateRepository {
  private readonly dataAccess = inject(LocalStorageDataAccess);
  private readonly userInfo = inject(UserInfoService);

  // Templates are per-user: namespace the localStorage key by the logged-in user id
  // so one user's templates don't leak to the next on a shared browser.
  private storageKey(): string {
    const userId = this.userInfo.value()?.userId;
    return userId != null ? `${KEY_PREFIX}_${userId}` : KEY_PREFIX;
  }

  private loadAll(): FilterTemplate[] {
    return this.dataAccess.get<FilterTemplate[]>({ key: this.storageKey() }) ?? [];
  }

  private saveAll(templates: FilterTemplate[]): void {
    this.dataAccess.set<FilterTemplate[]>({ key: this.storageKey() }, templates);
  }

  override async loadGroup(groupKey: string): Promise<FilterTemplate[]> {
    return this.loadAll().filter((o) => o.groupKey === groupKey);
  }

  override async create(template: FilterTemplate): Promise<FilterTemplate> {
    const templates = this.loadAll();
    if (templates.some((o) => o.id === template.id))
      throw new Error(
        `[LocalStorageFilterTemplateRepository:create] Template with id ${template.id} already exists`
      );
    // local storage only ever holds personal, writable templates
    const created: FilterTemplate = { ...template, scope: 'personal', readonly: false };
    this.saveAll([...templates, created]);
    return created;
  }

  override async update(id: string, template: FilterTemplate): Promise<FilterTemplate> {
    const templates = this.loadAll();
    const index = templates.findIndex((o) => o.id === id);
    if (index === -1)
      throw new Error(
        `[LocalStorageFilterTemplateRepository:update] Template with id ${id} not found`
      );
    templates[index] = template;
    this.saveAll(templates);
    return template;
  }

  override async patch(id: string, changes: Partial<FilterTemplate>): Promise<FilterTemplate> {
    const templates = this.loadAll();
    const index = templates.findIndex((o) => o.id === id);
    if (index === -1)
      throw new Error(
        `[LocalStorageFilterTemplateRepository:patch] Template with id ${id} not found`
      );
    const patched = { ...templates[index], ...changes };
    templates[index] = patched;
    this.saveAll(templates);
    return patched;
  }

  override async delete(id: string): Promise<FilterTemplate> {
    const templates = this.loadAll();
    const index = templates.findIndex((o) => o.id === id);
    if (index === -1)
      throw new Error(
        `[LocalStorageFilterTemplateRepository:delete] Template with id ${id} not found`
      );
    const [deleted] = templates.splice(index, 1);
    this.saveAll(templates);
    return deleted;
  }
}
